import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { attendanceService } from 'services/api';

/**
 * F-26 (2026-09-29): presencia del asistente en la asamblea a nivel de TODA la app.
 *
 * Antes el latido vivía dentro de la pantalla de votación (UserDashboard): al
 * navegar a "Mis unidades" o "Mi perfil" la pantalla se desmontaba y mandaba un
 * "Salir" automático, y con la pestaña oculta el latido se pausaba — la persona
 * salía del cuórum en vivo aunque siguiera en la asamblea (incluso con la
 * reunión de Zoom abierta, que ya vivía a nivel de app).
 *
 * Ahora, mientras la persona tenga una asamblea confirmada ("Participar"):
 *   - se manda un latido cada LATIDO_MS en cualquier pantalla de la app y
 *     también con la pestaña oculta (el servidor la da por retirada tras 5 min
 *     sin latido; en presencial no vence);
 *   - "Salir" solo al pulsar el botón, al cerrar sesión o al cerrar la pestaña
 *     (pagehide). Navegar dentro de la app ya no cuenta como salir.
 * Una asamblea por dispositivo/pestaña: iniciar otra (selector de F-24) sale
 * de la anterior.
 */
const LATIDO_MS = 60 * 1000;
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Recargar (F5) dispara el mismo `pagehide` que cerrar la pestaña, así que el
// servidor la da por retirada. Para distinguir las dos cosas, `pagehide` deja
// una marca en sessionStorage (sobrevive a recargar, NO a cerrar la pestaña) y
// la pantalla, al volver a cargar, reingresa sola si la marca tiene menos de
// VENTANA_RECARGA_MS (UserDashboard: consumirMarcaRecarga).
const MARCA_RECARGA = 'eventify_presencia_recarga';
const VENTANA_RECARGA_MS = 20 * 1000;
// La marca lleva el id del usuario: solo le sirve a quien la dejó (otra persona que inicie sesión en la misma pestaña no hereda el reingreso).
const idUsuario = () => { try { return JSON.parse(localStorage.getItem('user'))?.id ?? null; } catch { return null; } };

const PresenciaContext = createContext(null);

export function PresenciaProvider({ children }) {
  const { user } = useAuth();
  const asmRef   = useRef(null);   // asamblea donde está participando esta pestaña
  const tokenRef = useRef(null);   // capturado al iniciar: sirve aunque el logout ya borró localStorage
  const timerRef = useRef(null);

  const latido = useCallback(() => {
    if (asmRef.current) attendanceService.heartbeat(asmRef.current).catch(() => {});
  }, []);

  // "Salir" con fetch + keepalive: funciona aunque la pestaña se esté cerrando.
  const enviarSalida = useCallback((asmId) => {
    if (!asmId || !tokenRef.current) return;
    try {
      fetch(`${API}/attendances/leave/${asmId}`, {
        method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${tokenRef.current}` },
      }).catch(() => {});
    } catch { /* noop */ }
  }, []);

  const detenerTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  // Empieza (o cambia de) asamblea. Idempotente para la misma asamblea.
  const iniciar = useCallback((asmId) => {
    if (!asmId) return;
    if (asmRef.current === asmId && timerRef.current) return;
    if (asmRef.current && asmRef.current !== asmId) enviarSalida(asmRef.current);
    asmRef.current   = asmId;
    tokenRef.current = localStorage.getItem('token');
    detenerTimer();
    latido();
    timerRef.current = setInterval(latido, LATIDO_MS);
  }, [latido, enviarSalida]);

  // Deja de mandar latidos SIN marcar salida (la asamblea terminó o ya no está confirmada).
  const detener = useCallback((asmId) => {
    if (asmId && asmRef.current !== asmId) return;
    detenerTimer();
    asmRef.current = null;
  }, []);

  // Salida explícita (botón "Salir de la asamblea").
  const salir = useCallback(async () => {
    const asmId = asmRef.current;
    detener();
    if (asmId) { try { await attendanceService.leave(asmId); } catch { /* noop */ } }
  }, [detener]);

  // Cerrar la pestaña/navegador → salida. Volver a la pestaña → latido inmediato
  // (el navegador frena o detiene los relojes en segundo plano).
  useEffect(() => {
    const alCerrar = () => {
      const asmId = asmRef.current;
      if (asmId) {
        try { sessionStorage.setItem(MARCA_RECARGA, JSON.stringify({ asmId, ts: Date.now(), uid: idUsuario() })); } catch { /* noop */ }
      }
      enviarSalida(asmId);
    };
    const alVolver = () => { if (document.visibilityState === 'visible') latido(); };
    window.addEventListener('pagehide', alCerrar);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.removeEventListener('pagehide', alCerrar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [enviarSalida, latido]);

  // Cerrar sesión → salida.
  useEffect(() => {
    if (!user && asmRef.current) {
      try { sessionStorage.removeItem(MARCA_RECARGA); } catch { /* noop */ }
      enviarSalida(asmRef.current);
      detener();
    }
  }, [user, enviarSalida, detener]);

  useEffect(() => () => detenerTimer(), []);

  // ¿La página se acaba de recargar estando en esta asamblea? Lee y borra la marca.
  const consumirMarcaRecarga = useCallback((asmId) => {
    try {
      const raw = sessionStorage.getItem(MARCA_RECARGA);
      if (!raw) return false;
      sessionStorage.removeItem(MARCA_RECARGA);
      const m = JSON.parse(raw);
      return m.uid != null && m.uid === idUsuario() && String(m.asmId) === String(asmId) && Date.now() - m.ts <= VENTANA_RECARGA_MS;
    } catch { return false; }
  }, []);

  const valor = useMemo(() => ({ iniciar, detener, salir, consumirMarcaRecarga }), [iniciar, detener, salir, consumirMarcaRecarga]);
  return <PresenciaContext.Provider value={valor}>{children}</PresenciaContext.Provider>;
}

export const usePresencia = () => useContext(PresenciaContext);
